import type { ItemTabelaPreco, PassoProduto, ProdutoCatalogo, SubPassoProduto } from "@/api"
import { ehBlocoDeAdicionais } from "@/lib/adicional-por-sabor"

/** Seleção de um passo: idTabelaPrecoItem → quantidade escolhida. */
export type SelecaoPasso = Record<number, number>

/**
 * Seleções do produto inteiro. Passo comum: `idProdutoPasso`. Adicionais de uma opção:
 * `chaveAdicional(idPassoPai, idSubPasso, idItemPai)`, porque o mesmo sub-passo se repete por sabor.
 */
export type SelecoesPassos = Record<string, SelecaoPasso>

export interface PassoSelecionadoItem {
    idTabelaPrecoItem: number
    idProduto: number
    nome: string
    quantidade: number
    valor: number
}

/** Passo já resolvido (o que vai pro carrinho, em qualquer modo). */
export interface PassoSelecionado {
    idProdutoPasso: number
    descricao: string | null
    valorPasso: number
    /** Só no bloco de adicionais de uma opção: o passo pai e a opção (sabor) a que ele pertence. */
    idProdutoPassoPai?: number
    idItemPai?: number
    itens: PassoSelecionadoItem[]
}

/** A opção escolhida num passo com sub-passo, dona de uma etapa de adicionais. */
export interface OpcaoPai {
    idProdutoPassoPai: number
    idItemPai: number
    nome: string
    /** Parte do sabor na pizza, que multiplica o preço cheio do adicional (½ → metade do preço). */
    fracao: number
}

/** Etapa do wizard: um passo do produto ou os adicionais de uma opção escolhida nele. */
export interface EtapaPasso {
    /** Chave da seleção desta etapa em `SelecoesPassos`. */
    chave: string
    passo: PassoProduto
    pai: OpcaoPai | null
}

export function calcularValorPasso(passo: PassoProduto, selecao: SelecaoPasso): number {
    const itensSel = (passo.tabelaPreco?.itens ?? []).filter((it) => (selecao[it.id] ?? 0) > 0)

    if (itensSel.length === 0) return 0

    if (passo.tipoCalculo === 'MAIOR_VALOR') return Math.max(...itensSel.map((it) => it.valor))

    if (passo.tipoCalculo === 'FRACAO') {
        return itensSel.reduce((s, it) => s + it.valor, 0) / itensSel.length
    }

    return itensSel.reduce((s, it) => s + it.valor * (selecao[it.id] ?? 0), 0)
}

export function chaveAdicional(idPassoPai: number, idSubPasso: number, idItemPai: number): string {
    return `${idPassoPai}>${idSubPasso}:${idItemPai}`
}

/** Parte da opção no passo pai: quantidade dela ÷ soma do passo (2 sabores → ½; sabor único → 1). */
export function fracaoDaOpcao(passo: PassoProduto, selecao: SelecaoPasso, idItemPai: number): number {
    const total = (passo.tabelaPreco?.itens ?? []).reduce((s, it) => s + (selecao[it.id] ?? 0), 0)

    return total > 0 ? (selecao[idItemPai] ?? 0) / total : 0
}

/** Adicional por sabor é opt-in do cadastro: sub-passo de outro tipo não vira etapa nem bloco (o backend nem o emite). */
export function subPassoPorSabor(passo: PassoProduto): SubPassoProduto | null {
    const sub = passo.subPasso

    return sub?.tipoCalculo === 'PROPORCIONAL_AO_SABOR' ? sub : null
}

export function calcularValorAdicionais(subPasso: SubPassoProduto, selecao: SelecaoPasso, fracao: number): number {
    return (subPasso.tabelaPreco?.itens ?? []).reduce((s, it) => s + it.valor * (selecao[it.id] ?? 0) * fracao, 0)
}

const FRACOES_COMUNS: Record<string, string> = { '1/2': '½', '1/3': '⅓', '2/3': '⅔', '1/4': '¼', '3/4': '¾' }

/** Fração do sabor para o cliente ler ("½", "⅓", "2/5"); vazio quando o adicional vale o item inteiro. */
export function textoFracao(fracao: number): string {
    if (fracao <= 0 || fracao >= 1) return ''

    for (let denominador = 2; denominador <= 12; denominador += 1) {
        const numerador = Math.round(fracao * denominador)

        if (Math.abs(numerador / denominador - fracao) < 1e-9) {
            const texto = `${numerador}/${denominador}`
            return FRACOES_COMUNS[texto] ?? texto
        }
    }

    return `${Math.round(fracao * 100)}%`
}

export function temPassos(produto: ProdutoCatalogo): boolean {
    return (produto.passos?.length ?? 0) > 0
}

/**
 * Passo que chegou SEM nenhuma opção porque a loja pausou todas (catálogo v6 em diante).
 * Antes ele nem vinha no payload, e o produto abria sem a escolha obrigatória.
 */
export function passoIndisponivel(passo: PassoProduto): boolean {
    return passo.indisponivel === true
}

/**
 * O primeiro passo OBRIGATÓRIO que ficou sem opção — ou null, se não há nenhum.
 * Devolve o passo, e não um booleano, porque o aviso ao cliente cita o nome dele.
 * Passo opcional pausado não bloqueia nada: o cliente segue sem escolher.
 */
export function passoObrigatorioIndisponivel(produto: ProdutoCatalogo): PassoProduto | null {
    return (produto.passos ?? []).find((p) => passoIndisponivel(p) && p.quantidadeMinima > 0) ?? null
}

/** Produto que não pode ser pedido agora: tem passo obrigatório sem nenhuma opção. */
export function produtoIndisponivel(produto: ProdutoCatalogo): boolean {
    return passoObrigatorioIndisponivel(produto) !== null
}

/** Nome do passo como o cliente vê (o cadastro do cardápio vence a descrição interna). */
export function nomeDoPasso(passo: PassoProduto): string {
    return passo.descricaoCardapio?.trim() || passo.tabelaPreco?.descricao?.trim() || 'opções'
}

function opcoesEscolhidas(passo: SubPassoProduto, selecao: SelecaoPasso): ItemTabelaPreco[] {
    return (passo.tabelaPreco?.itens ?? []).filter((it) => (selecao[it.id] ?? 0) > 0)
}

function itensEscolhidos(passo: SubPassoProduto, selecao: SelecaoPasso): PassoSelecionadoItem[] {
    return opcoesEscolhidas(passo, selecao).map((it) => ({
        idTabelaPrecoItem: it.id,
        idProduto: it.idProduto,
        nome: it.nomeProduto,
        quantidade: selecao[it.id] ?? 0,
        valor: it.valor,
    }))
}

function subPassoComOpcoes(passo: PassoProduto): SubPassoProduto | null {
    const sub = subPassoPorSabor(passo)
    if (!sub || sub.quantidadeMaxima <= 0 || (sub.tabelaPreco?.itens.length ?? 0) === 0) return null

    return sub
}

/**
 * Etapas do wizard na ordem dos passos. Com `adicionalPorOpcao`, o passo que tem sub-passo
 * proporcional ganha logo depois dele uma etapa de adicionais para cada opção escolhida.
 */
export function montarEtapas(passos: PassoProduto[], selecoes: SelecoesPassos, adicionalPorOpcao: boolean): EtapaPasso[] {
    return passos.flatMap((passo) => {
        const etapa: EtapaPasso = { chave: String(passo.id), passo, pai: null }
        const sub = adicionalPorOpcao ? subPassoComOpcoes(passo) : null
        if (!sub) return [etapa]

        const selecao = selecoes[passo.id] ?? {}
        const adicionais = opcoesEscolhidas(passo, selecao).map((opcao): EtapaPasso => ({
            chave: chaveAdicional(passo.id, sub.id, opcao.id),
            // Sempre opcional, mesmo com mínimo no cadastro: no PDV abrir os adicionais do sabor é escolha.
            passo: { ...sub, quantidadeMinima: 0 },
            pai: {
                idProdutoPassoPai: passo.id,
                idItemPai: opcao.id,
                nome: opcao.nomeProduto,
                fracao: fracaoDaOpcao(passo, selecao, opcao.id),
            },
        }))

        return [etapa, ...adicionais]
    })
}

function blocosDeAdicionais(passo: PassoProduto, selecao: SelecaoPasso, selecoes: SelecoesPassos): PassoSelecionado[] {
    const sub = subPassoPorSabor(passo)
    if (!sub) return []

    return opcoesEscolhidas(passo, selecao).flatMap((opcao) => {
        const selecaoAdicional = selecoes[chaveAdicional(passo.id, sub.id, opcao.id)] ?? {}
        const itens = itensEscolhidos(sub, selecaoAdicional)
        if (itens.length === 0) return []

        const fracao = fracaoDaOpcao(passo, selecao, opcao.id)

        return [{
            idProdutoPasso: sub.id,
            descricao: sub.tabelaPreco?.descricao ?? null,
            idProdutoPassoPai: passo.id,
            idItemPai: opcao.id,
            valorPasso: calcularValorAdicionais(sub, selecaoAdicional, fracao),
            itens,
        }]
    })
}

/** Adicionais escolhidos para uma opção do passo pai, na ordem da tabela — o que some junto quando ela é desmarcada. */
export function nomesDosAdicionais(passo: PassoProduto, selecoes: SelecoesPassos, idItemPai: number): string[] {
    const sub = subPassoPorSabor(passo)
    if (!sub) return []

    return opcoesEscolhidas(sub, selecoes[chaveAdicional(passo.id, sub.id, idItemPai)] ?? {}).map((it) => it.nomeProduto)
}

/** Passos escolhidos e, logo após cada passo, um bloco de adicionais por opção que tem adicional. */
export function montarPassos(produto: ProdutoCatalogo, selecoes: SelecoesPassos): PassoSelecionado[] {
    return produto.passos.flatMap((passo) => {
        const selecao = selecoes[passo.id] ?? {}
        const itens = itensEscolhidos(passo, selecao)
        if (itens.length === 0) return []

        const principal: PassoSelecionado = {
            idProdutoPasso: passo.id,
            descricao: passo.tabelaPreco?.descricao ?? null,
            valorPasso: calcularValorPasso(passo, selecao),
            itens,
        }

        return [principal, ...blocosDeAdicionais(passo, selecao, selecoes)]
    })
}

export function precoUnitario(produto: ProdutoCatalogo, passos: PassoSelecionado[]): number {
    const extras = passos.reduce((s, p) => s + p.valorPasso, 0)

    return Number((produto.valor + extras).toFixed(2))
}

/**
 * Chave de igualdade entre itens do carrinho: mesmo produto e mesmas escolhas.
 * O tipo dos passos é estrutural para servir aos dois carrinhos (kiosk e cardápio).
 */
export function assinaturaItem(
    idProduto: number,
    passos: { idProdutoPasso: number; idProdutoPassoPai?: number; idItemPai?: number; itens: { idTabelaPrecoItem: number; quantidade: number }[] }[],
): string {
    const norm = passos
        .flatMap((p) => {
            const passo = ehBlocoDeAdicionais(p) ? `${p.idProdutoPasso}@${p.idProdutoPassoPai}:${p.idItemPai}` : `${p.idProdutoPasso}`
            return p.itens.map((it) => `${passo}:${it.idTabelaPrecoItem}x${it.quantidade}`)
        })
        .sort()
        .join('|')

    return `${idProduto}::${norm}`
}

/**
 * Seleção inicial do wizard: ao editar, repõe o que já estava salvo; num item
 * novo, pré-marca os itens `adicionarAutomaticamente` da tabela de preço.
 */
export function selecoesIniciais(
    passos: PassoProduto[],
    passosSalvos: PassoSelecionado[] | null,
): SelecoesPassos {
    const inicial: SelecoesPassos = {}

    for (const passo of passos) {
        const selecao: SelecaoPasso = {}

        if (passosSalvos) {
            const salvo = passosSalvos.find((p) => p.idProdutoPasso === passo.id && !ehBlocoDeAdicionais(p))
            salvo?.itens.forEach((it) => (selecao[it.idTabelaPrecoItem] = it.quantidade))
        } else {
            ; (passo.tabelaPreco?.itens ?? []).forEach((it) => {
                if (it.adicionarAutomaticamente) selecao[it.id] = it.quantidadeAutomatica ?? 1
            })
        }

        inicial[passo.id] = selecao
    }

    for (const bloco of passosSalvos ?? []) {
        if (!ehBlocoDeAdicionais(bloco)) continue

        const selecao: SelecaoPasso = {}
        bloco.itens.forEach((it) => (selecao[it.idTabelaPrecoItem] = it.quantidade))
        inicial[chaveAdicional(bloco.idProdutoPassoPai, bloco.idProdutoPasso, bloco.idItemPai)] = selecao
    }

    return inicial
}
